import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './ConflictResolutionModal.css';

/**
 * Modal to prompt user when merge diff fails
 * Shows when traditional diff fails and user needs to resolve manually
 */
const ConflictResolutionModal = ({ 
  isOpen, 
  fileName, 
  error,
  onManualResolve, 
  onCancel,
  failedFiles = []
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="conflict-modal-overlay">
      <div className="conflict-modal">
        <div className="conflict-modal-header">
          <div className="conflict-modal-icon">⚠️</div>
          <h2>Merge Conflict Detected</h2>
        </div>
        
        <div className="conflict-modal-body">
          <p className="conflict-modal-main-text">
            Unable to automatically apply changes to <strong>{fileName}</strong>.
          </p>
          
          {error && (
            <div className="conflict-modal-error-box">
              <div className="error-label">Error Details:</div>
              <div className="error-message">{error}</div>
            </div>
          )}

          {failedFiles.length > 0 && (
            <div className="conflict-modal-warning">
              <strong>Files affected:</strong>
              <ul className="failed-files-list">
                {failedFiles.map((file, index) => (
                  <li key={index}>{file}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="conflict-modal-info-box">
            <p><strong>What happened?</strong></p>
            <p>The code changes couldn't be automatically merged because the file structure has changed or conflicts exist.</p>
            
            <p><strong>What should you do?</strong></p>
            <ul>
              <li>Review the file manually and apply changes</li>
              <li>Use your IDE's diff/merge tools to resolve conflicts</li>
              <li>Check if the file was modified elsewhere</li>
            </ul>
          </div>
        </div>
        
        <div className="conflict-modal-footer">
          <button 
            className="conflict-modal-btn conflict-modal-btn-cancel"
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button 
            className="conflict-modal-btn conflict-modal-btn-resolve"
            onClick={onManualResolve}
            type="button"
          >
            Resolve Manually
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

ConflictResolutionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  fileName: PropTypes.string.isRequired,
  error: PropTypes.string,
  onManualResolve: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  failedFiles: PropTypes.arrayOf(PropTypes.string)
};

ConflictResolutionModal.defaultProps = {
  error: null,
  failedFiles: []
};

export default ConflictResolutionModal;
